/** Form values for description-append fields use this prefix (not sent to Opportunity columns). */
export const DESC_APPEND_FIELD_PREFIX = "__desc_";

/** Field types that can be appended into the request description. */
export const DESCRIPTION_APPEND_INPUT_TYPES = [
  "TEXT",
  "NUMBER",
  "BOOLEAN",
  "SELECT",
] as const;

export type DescriptionAppendInputType =
  (typeof DESCRIPTION_APPEND_INPUT_TYPES)[number];

export type SubCategoryFieldConfigLike = {
  field: string;
  label: string;
  type: string;
  order: number;
  appendToDescription?: boolean | null;
  isRequired?: boolean | null;
  isVisible?: boolean | null;
  options?: string[] | null;
};

const RESERVED_FIELD_PATTERN = /^__desc_/;

export function descriptionAppendFormKey(field: string): string {
  return `${DESC_APPEND_FIELD_PREFIX}${field}`;
}

export function isDescriptionAppendFormKey(key: string): boolean {
  return key.startsWith(DESC_APPEND_FIELD_PREFIX);
}

export function isReservedSubCategoryFieldSlug(field: string): boolean {
  return RESERVED_FIELD_PATTERN.test(field);
}

export function isDescriptionAppendFieldType(type: string): boolean {
  return (DESCRIPTION_APPEND_INPUT_TYPES as readonly string[]).includes(type);
}

export function getVisibleSubCategoryFieldConfigs<T extends SubCategoryFieldConfigLike>(
  configs: readonly T[] | undefined,
): T[] {
  return (configs ?? []).filter((c) => c.isVisible !== false);
}

export function getDescriptionAppendFieldConfigs<T extends SubCategoryFieldConfigLike>(
  configs: readonly T[] | undefined,
): T[] {
  return getVisibleSubCategoryFieldConfigs(configs).filter((c) => c.appendToDescription);
}

/**
 * Project-link fields are added on top of subcategory fields.
 * When both define the same `field` slug, the project config replaces the subcategory one.
 */
export function mergeRequestFieldConfigs<
  A extends SubCategoryFieldConfigLike,
  B extends SubCategoryFieldConfigLike,
>(
  subCategoryConfigs: readonly A[] | undefined,
  teamConfigs: readonly B[] | undefined,
): (A | B)[] {
  const byField = new Map<string, A | B>();
  for (const config of subCategoryConfigs ?? []) {
    byField.set(config.field, config);
  }
  for (const config of teamConfigs ?? []) {
    byField.set(config.field, config);
  }
  return [...byField.values()];
}

/** Ensures API/DB rows always expose the new fields (safe before migration is applied). */
export function normalizeSubCategoryFieldConfigRow<
  T extends SubCategoryFieldConfigLike,
>(row: T): T {
  return {
    ...row,
    appendToDescription: row.appendToDescription === true,
    options: Array.isArray(row.options) ? row.options : [],
  };
}

/**
 * New weme-app clients always send this key (even `{}`).
 * `undefined` = legacy client → server must not enforce append rules or re-merge.
 */
export function shouldProcessDescriptionAppendAnswers(
  descriptionAppendAnswers: unknown,
): boolean {
  return descriptionAppendAnswers !== undefined && descriptionAppendAnswers !== null;
}

export function formatDescriptionAppendAnswer(
  type: string,
  value: unknown,
): string {
  if (value === undefined || value === null) return "";
  if (type === "IMAGE") return "";
  if (type === "BOOLEAN") {
    if (typeof value !== "boolean") return "";
    return value ? "כן" : "לא";
  }
  if (type === "NUMBER") {
    if (value === "" || value === null || value === undefined)
      return "";
    if (typeof value === "number") {
      return Number.isFinite(value) ? String(value) : "";
    }
    const n = Number(value);
    return Number.isNaN(n) || !Number.isFinite(n) ? "" : String(n);
  }
  return String(value).trim();
}

/** Answers keyed by field slug (as stored in DB), for API payload / server merge. */
export function collectDescriptionAppendAnswers(
  configs: readonly SubCategoryFieldConfigLike[],
  formValues: Record<string, unknown>,
): Record<string, unknown> {
  const answers: Record<string, unknown> = {};
  for (const config of getDescriptionAppendFieldConfigs(configs)) {
    const key = descriptionAppendFormKey(config.field);
    const raw = formValues[key];

    switch (config.type) {
      case "BOOLEAN":
        if (typeof raw !== "boolean") continue;
        answers[config.field] = raw;
        break;

      case "NUMBER": {
        if (raw === undefined || raw === null || raw === "") continue;
        const n =
          typeof raw === "number" ? raw : Number(String(raw).trim());
        if (!Number.isFinite(n)) continue;
        answers[config.field] = n;
        break;
      }

      case "SELECT":
      case "TEXT": {
        if (raw === undefined || raw === null || raw === "") continue;
        const s = String(raw).trim();
        if (!s) continue;
        answers[config.field] = s;
        break;
      }

      default:
        break;
    }
  }
  return answers;
}

export function validateRequiredDescriptionAppendAnswers(
  configs: readonly SubCategoryFieldConfigLike[],
  answers: Record<string, unknown>,
): string | null {
  for (const config of getDescriptionAppendFieldConfigs(configs)) {
    if (!config.isRequired) continue;
    const formatted = formatDescriptionAppendAnswer(
      config.type,
      answers[config.field],
    );
    if (!formatted) {
      return `יש למלא את השדה "${config.label}"`;
    }
  }
  return null;
}

/**
 * Validates answer values that are actually present before merge (tampering-safe on server).
 * Required-empty is handled separately by {@link validateRequiredDescriptionAppendAnswers}.
 */
export function validateDescriptionAppendAnswerValues(
  configs: readonly SubCategoryFieldConfigLike[],
  answers: Record<string, unknown>,
): string | null {
  for (const config of getDescriptionAppendFieldConfigs(configs)) {
    const raw = answers[config.field];
    if (raw === undefined || raw === null) continue;

    switch (config.type) {
      case "SELECT": {
        const str =
          typeof raw === "string" ? raw.trim() : String(raw).trim();
        if (!str) continue;
        const opts = config.options ?? [];
        if (opts.length > 0 && !opts.includes(str)) {
          return `יש לבחור אפשרות מהרשימה בשדה "${config.label}"`;
        }
        break;
      }
      case "NUMBER": {
        const n =
          typeof raw === "number" ? raw : Number(String(raw).trim());
        if (!Number.isFinite(n)) {
          return `יש להזין מספר תקין בשדה "${config.label}"`;
        }
        break;
      }
      case "BOOLEAN": {
        if (typeof raw !== "boolean") {
          return `ערך לא חוקי בשדה "${config.label}"`;
        }
        break;
      }
      case "TEXT": {
        if (typeof raw !== "string") {
          return `ערך לא חוקי בשדה "${config.label}"`;
        }
        break;
      }
      default:
        break;
    }
  }
  return null;
}

function resolveAppendValues(
  configs: readonly SubCategoryFieldConfigLike[],
  source: Record<string, unknown>,
  mode: "form" | "slug",
): Record<string, unknown> {
  if (mode === "form") return source;
  const values: Record<string, unknown> = {};
  for (const config of getDescriptionAppendFieldConfigs(configs)) {
    if (config.field in source) {
      values[descriptionAppendFormKey(config.field)] = source[config.field];
    }
  }
  return values;
}

/**
 * Appends configured Q&A lines after the user's free-text description.
 * Format: blank line, then `label: answer` per non-empty field (sorted by order).
 *
 * @param mode `form` - keys are `__desc_${field}`; `slug` - keys are field slugs from DB.
 */
export function appendDescriptionFieldAnswers(
  userDescription: string,
  configs: readonly SubCategoryFieldConfigLike[],
  source: Record<string, unknown>,
  mode: "form" | "slug" = "form",
): string {
  const appendConfigs = getDescriptionAppendFieldConfigs(configs);
  const formValues = resolveAppendValues(configs, source, mode);
  const base = userDescription.trim();
  const lines = [...appendConfigs]
    .sort((a, b) => a.order - b.order)
    .map((config) => {
      const answer = formatDescriptionAppendAnswer(
        config.type,
        formValues[descriptionAppendFormKey(config.field)],
      );
      if (!answer) return null;
      return `${config.label}: ${answer}`;
    })
    .filter((line): line is string => line != null);

  if (lines.length === 0) return base;
  return base ? `${base}\n\n${lines.join("\n")}` : lines.join("\n");
}
